import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Autocomplete,
  Chip,
} from '@mui/material';
import {
  Add,
  QrCode,
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store';
import { closeDialog, addNotification } from '../../store/slices/uiSlice';
import { addItem } from '../../store/slices/inventorySlice';
import { FirestoreService } from '../../firebase/firestoreService';
import { InventoryItem } from '../../types/models';
import { v4 as uuidv4 } from 'uuid';

const AddItemDialog: React.FC = () => {
  const dispatch = useDispatch();
  const { dialog } = useSelector((state: RootState) => state.ui);
  const { groups } = useSelector((state: RootState) => state.inventory);
  const { organization } = useSelector((state: RootState) => state.auth);
  
  const [itemName, setItemName] = useState('');
  const [itemGroup, setItemGroup] = useState<string | null>(null);
  const [generateQR, setGenerateQR] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isOpen = dialog.open && dialog.type === 'add-item';

  const handleClose = () => {
    dispatch(closeDialog());
    setItemName('');
    setItemGroup(null);
    setGenerateQR(true);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!itemName.trim()) {
      setError('Item name is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const itemID = uuidv4();
      const qrFileName = generateQR ? `qr-${itemID}.png` : '';

      const newItem: Omit<InventoryItem, 'id'> = {
        itemID,
        itemName: itemName.trim(),
        itemQRFileName: qrFileName,
        checkedOut: false,
        checkOutHistory: [],
        itemGroup: itemGroup || '',
        lastUpdated: Date.now() / 1000,
        ...(organization?.id ? { organizationID: organization.id } : {}),
      };

      const docId = await FirestoreService.addInventoryItem(newItem);

      // Update local state immediately for optimistic UI
      const addedItem: InventoryItem = {
        id: docId,
        ...newItem,
      };
      dispatch(addItem(addedItem));

      dispatch(addNotification({
        message: `${itemName} added successfully`,
        type: 'success',
      }));

      handleClose();
    } catch (error: any) {
      console.error('Error adding item:', error);
      setError(error.message || 'Failed to add item');
      dispatch(addNotification({
        message: `Failed to add ${itemName}`,
        type: 'error',
      }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Add color="primary" />
          <Typography variant="h6">Add New Item</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
          <TextField
            autoFocus
            margin="normal"
            label="Item Name"
            fullWidth
            required
            value={itemName}
            onChange={(e) => setItemName(e.target.value)}
            placeholder="Enter item name"
            disabled={loading}
            helperText="Give your item a clear, descriptive name"
          />

          <Autocomplete
            value={itemGroup}
            onChange={(event, newValue) => setItemGroup(newValue)}
            inputValue={itemGroup || ''}
            onInputChange={(event, newInputValue) => setItemGroup(newInputValue)}
            options={groups}
            freeSolo
            renderTags={(value: readonly string[], getTagProps) =>
              value.map((option: string, index: number) => (
                <Chip variant="outlined" label={option} {...getTagProps({ index })} />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                margin="normal"
                label="Group (Optional)"
                placeholder="Enter or select a group"
                disabled={loading}
                helperText="Group similar items together for better organization"
              />
            )}
          />

          <FormControlLabel
            control={
              <Switch
                checked={generateQR}
                onChange={(e) => setGenerateQR(e.target.checked)}
                disabled={loading}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <QrCode />
                <span>Generate QR Code</span>
              </Box>
            }
            sx={{ mt: 2 }}
          />

          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            QR codes make it easy to check items in and out using a camera or scanner
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !itemName.trim()}
          startIcon={loading ? <CircularProgress size={20} /> : <Add />}
        >
          {loading ? 'Adding...' : 'Add Item'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddItemDialog;